import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

export interface ToastProps {
  id: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  icon?: string | React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  message,
  type = 'info',
  icon,
  action,
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300); // Wait for exit animation
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'warning':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'error':
        return 'bg-red-50 text-red-800 border-red-200';
      default:
        return 'bg-white text-gray-800 border-gray-200';
    }
  };

  const defaultIcons = {
    info: '💬',
    success: '✅',
    warning: '⚠️',
    error: '❌'
  };

  const displayIcon = icon || defaultIcons[type];

  return (
    <div
      className={`
        flex items-center gap-3 p-4 rounded-lg border shadow-lg
        transform transition-all duration-300 ease-out mb-3
        ${getTypeStyles()}
        ${isVisible 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
        }
      `}
      style={{
        animation: isVisible ? 'slideInRight 0.3s ease-out' : undefined
      }}
    >
      {/* Icon */}
      {displayIcon && (
        <div className="flex-shrink-0 text-xl">
          {typeof displayIcon === 'string' ? displayIcon : displayIcon}
        </div>
      )}

      {/* Content */}
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>

      {/* Action */}
      {action && (
        <button
          onClick={action.onClick}
          className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
        >
          {action.label}
        </button>
      )}

      {/* Close button */}
      <button
        onClick={handleClose}
        className="flex-shrink-0 p-1 hover:bg-black/5 rounded transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Container Component
export const ToastContainer: React.FC<{ toasts: ToastProps[] }> = ({ toasts }) => {
  return (
    <div
      className="fixed top-14 left-1/2 -translate-x-1/2 cockpit-toast-layer max-h-[40vh] overflow-y-auto max-w-sm w-[calc(100%-2.5rem)] pointer-events-none"
      style={{ maxWidth: '360px' }}
    >
      <div className="pointer-events-auto">
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} />
        ))}
      </div>
    </div>
  );
};

// Toast Hook
export const useToast = () => {
  const [toasts, setToasts] = useState<Omit<ToastProps, 'onClose'>[]>([]);

  const showToast = (toast: Omit<ToastProps, 'id' | 'onClose'>) => {
    const id = Date.now().toString();
    const newToast = { ...toast, id };
    
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const toastProps = toasts.map(toast => ({
    ...toast,
    onClose: removeToast
  }));

  return { toasts: toastProps, showToast };
};

export default Toast;